import { useCallback, useMemo, useState } from 'react';

export const useModals = <T extends string>(names: readonly T[]) => {
  const [state, setState] = useState<Record<T, boolean>>(
    () =>
      Object.fromEntries(names.map((name) => [name, false])) as Record<
        T,
        boolean
      >
  );

  const open = useCallback(
    (name: T) => setState((state) => ({ ...state, [name]: true })),
    []
  );
  const close = useCallback(
    (name: T) => setState((state) => ({ ...state, [name]: false })),
    []
  );
  const closeAll = useCallback(
    () =>
      setState(
        Object.fromEntries(names.map((name) => [name, false])) as Record<
          T,
          boolean
        >
      ),
    [names]
  );
  const toggle = useCallback(
    (name: T) =>
      setState((state) => ({
        ...state,
        [name]: !state[name],
      })),
    []
  );
  const isOpen = useCallback((name: T) => state[name], [state]);
  const isOpenAny = useMemo(() => Object.values(state).some(Boolean), [state]);

  return useMemo(
    () => ({
      state,
      open,
      close,
      toggle,
      isOpen,
      isOpenAny,
      closeAll,
    }),
    [state, open, close, toggle, isOpen, isOpenAny, closeAll]
  );
};
